
/*
  Viewport detection
*/
document.querySelectorAll("[data-viewport-entered]").forEach((el) => {
  let marginBottom = -100;

  if (el.offsetHeight < 100 || el.dataset.viewportSmall === "true") {
    marginBottom = -30;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.dataset.viewportEntered = "true";
          observer.unobserve(entry.target);

          if (entry.target.dataset.videoAutoplay === "true") {
            entry.target.querySelectorAll("video").forEach((video) => {
              video.muted = true;
              video.play();
            });
          }

          if (entry.target.dataset.externalVideoLazyload === "true") {
            entry.target.querySelectorAll("iframe[data-src]").forEach((iframe) => {
              iframe.src = iframe.dataset.src;
              iframe.removeAttribute("data-src");
            });
          }
        }
      });
    },
    {
      rootMargin: `0px 0px ${marginBottom}px 0px`,
    },
  );

  observer.observe(el);
});


/*
  Carousel
*/
class CarouselComponent extends HTMLElement {
  constructor() {
    super();

    this.track = this.querySelector(".carousel-track");
    this.handleControls();
    this.handleControlsPosition();
    this.handlePagination();
  }

  handleControls() {
    this.querySelectorAll(".carousel-control").forEach((btn) => {
      btn.addEventListener("click", () => {
        const firstSlideOffset = this.querySelector(".carousel-slide:nth-child(1)")?.getBoundingClientRect().left;
        const secondSlideOffset = this.querySelector(".carousel-slide:nth-child(2)")?.getBoundingClientRect().left;
        const slideWidth = secondSlideOffset - firstSlideOffset;

        if (btn.name === "previous") {
          if (this.track.scrollLeft <= 16) {
            this.track.scrollTo({ left: this.track.scrollWidth, behavior: "smooth" });
          } else {
            this.track.scrollBy({ left: -slideWidth, behavior: "smooth" });
          }
        } else {
          if (this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 16) {
            this.track.scrollTo({ left: 0, behavior: "smooth" });
          } else {
            this.track.scrollBy({ left: slideWidth, behavior: "smooth" });
          }
        }
      });

      const updateControlsState = () => {
        if (this.track.querySelectorAll(".carousel-slide").length <= 1 || this.track.scrollWidth <= this.track.clientWidth) {
          btn.disabled = true;
        } else {
          btn.disabled = false;
        }
      };

      setTimeout(() => {
        updateControlsState();
      }, 1000);

      window.addEventListener('dialog:open', () => {
        updateControlsState();
      });

      window.addEventListener("resize", updateControlsState);
    });
  }

  async handleControlsPosition() {
    if (this.dataset.controlsAdaptToImage !== "true") return;

    await new Promise((resolve) => setTimeout(resolve, 400));

    const update = () => {
      const image = this.querySelector(".carousel-slide img") || this.querySelector(".carousel-slide .placeholder-svg");
      if (!image) return;
      const offset = image.clientHeight / 2;
      this.querySelectorAll(".carousel-control").forEach((btn) => {
        btn.style.top = `${offset}px`;
      });
    };
    update();

    window.addEventListener("resize", this.throttle(update, 150));
  }

  handlePagination() {
    const paginationCounter = this.querySelector(".carousel-pagination-counter");
    const paginationLine = this.querySelector(".carousel-pagination-line");
    if (!paginationCounter && !paginationLine) return;

    const totalSlides = this.querySelectorAll(".carousel-slide").length;

    const update = () => {
      if (!this.querySelector(".carousel-slide")) return

      const visibleSlides = Math.floor(this.track.clientWidth / this.querySelector(".carousel-slide").clientWidth);
      let firstVisibleSlide = Math.round(this.track.scrollLeft / this.querySelector(".carousel-slide").clientWidth) + visibleSlides;

      if (firstVisibleSlide > totalSlides) {
        firstVisibleSlide = totalSlides;
      }

      if (paginationCounter) {
        paginationCounter.querySelector("span").innerHTML = `${firstVisibleSlide}/${totalSlides}`;
      }
      if (paginationLine) {
        paginationLine.style.setProperty("--progress", `${(firstVisibleSlide / totalSlides) * 100}%`);
      }
    };

    setTimeout(() => {
      update();
    }, 1000);

    this.track.addEventListener("scroll", () => {
      this.throttle(update(), 150);
      this.debounce(update(), 50);
    });

    window.addEventListener("resize", this.throttle(update, 150));

    window.addEventListener("dialog:open", () => {
      update();
    });
  }

  throttle(func, limit) {
    let lastCall = 0;
    return function (...args) {
      const now = Date.now();
      if (now - lastCall >= limit) {
        lastCall = now;
        func.apply(this, args);
      }
    };
  }

  debounce(func, delay) {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  }
}
customElements.define("carousel-component", CarouselComponent);

/*
  Shoppable Videos
*/
class ShoppableVideos extends HTMLElement {
  constructor() {
    super()

    this.querySelectorAll('button.video-play-button').forEach(btn => {
      btn.addEventListener('click', () => {
        const video = btn.querySelector('video')
        this.playVideo(video)
      })
    })

    this.initializeVideoProgressBars()
    this.pauseVideosOnScroll()
  }

  initializeVideoProgressBars() {
    this.querySelectorAll('video').forEach(video => {
      const card = video.closest('.shoppable-videos-card')
      const progressBar = card.querySelector('.shoppable-videos-card-progress-bar')

      if (!progressBar) return

      // Update progress bar as video plays
      video.addEventListener('timeupdate', () => {
        if (video.duration) {
          const progress = (video.currentTime / video.duration) * 100
          progressBar.style.width = `${progress}%`
        }
      })

      // Reset progress bar when video ends
      video.addEventListener('ended', () => {
        progressBar.style.width = '0%'
        video.currentTime = 0
      })

      // Initialize progress bar when metadata loads
      video.addEventListener('loadedmetadata', () => {
        if (video.currentTime > 0 && video.duration) {
          const progress = (video.currentTime / video.duration) * 100
          progressBar.style.width = `${progress}%`
        }
      })
    })
  }

  playVideo(video) {
    // Pause other videos and reset them to beginning
    this.querySelectorAll('video').forEach(elem => {
      if (elem.dataset.index !== video.dataset.index) {
        elem.closest('.shoppable-videos-card').setAttribute('data-playing', 'false')
        elem.pause()
        elem.currentTime = 0
      }
    })

    const card = video.closest('.shoppable-videos-card')

    if (card.dataset.playing === 'true') {
      // Same video clicked - toggle play/pause
      card.setAttribute('data-playing', 'false')
      video.pause()
      video.blur()
    } else {
      // Start playing video
      card.setAttribute('data-playing', 'true')
      video.muted = false
      video.play()
      video.focus()
    }
  }

  pauseAllVideos() {
    this.querySelectorAll('video').forEach(video => {
      if (video.closest('.shoppable-videos-card').dataset.playing === 'true') {
        video.closest('.shoppable-videos-card').setAttribute('data-playing', 'false')
        video.pause()
        video.currentTime = 0
      }
    })
  }

  pauseVideosOnScroll() {
    this.querySelector('.carousel-track').addEventListener('scroll', () => {
      this.pauseAllVideos()
    })

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          this.pauseAllVideos()
        }
      })
    }, { threshold: 0.25 })

    observer.observe(this)
  }
}
customElements.define('shoppable-videos', ShoppableVideos);

/*
  Cart goal
*/
(function() {
  var goalEl = document.querySelector('[data-eg-cart-goal]');
  if (!goalEl) return;

  console.log('Initializing cart goal');

  var goalCents = parseInt(goalEl.dataset.goalCents, 10);
  var msgNotReached = goalEl.dataset.msgNotReached; // HTML string with [value] placeholder
  var msgReached = goalEl.dataset.msgReached;

  var messageEl = goalEl.querySelector('.eg-cart-goal__message');
  var barEl = goalEl.querySelector('.eg-cart-goal__bar');
  var barWrapEl = goalEl.querySelector('.eg-cart-goal__bar-wrap');

  function updateGoal(cartTotal) {
    var remaining = goalCents - cartTotal;
    var pct = Math.min(Math.round((cartTotal / goalCents) * 100), 100);
    var reached = remaining <= 0;

    // Update progress bar
    barEl.style.width = pct + '%';
    barWrapEl.setAttribute('aria-valuenow', pct);
    barEl.classList.toggle('eg-cart-goal__bar--complete', reached);

    // Update message
    messageEl.classList.toggle('eg-cart-goal__message--reached', reached);
    if (reached) {
      messageEl.innerHTML = msgReached;
    } else {
      var formatted = theme.Currency.formatMoney(remaining, theme.settings.moneyFormat);
      messageEl.innerHTML = msgNotReached.replace('[value]', formatted);
    }
  }

  document.addEventListener('cart:updated', function (evt) {
    var cart = evt.detail && evt.detail.cart;
    if (!cart) return;
    updateGoal(cart.total_price);
  });

  // cart:updated is NOT fired after ajaxProduct:added (only after quantity changes),
  // so we fetch /cart.js directly on add to keep the goal in sync.
  document.addEventListener('ajaxProduct:added', function () {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateGoal(cart.total_price); });
  });
})();

/*
  Cart upsells
*/
(function () {
  var upsellsEl = document.querySelector('[data-eg-cart-upsells]');
  if (!upsellsEl) return;

  function getCartProductIds(cart) {
    return (cart.items || []).map(function (item) { return item.product_id; });
  }

  function updateUpsells(cart) {
    var inCartIds = getCartProductIds(cart);
    var items = upsellsEl.querySelectorAll('[data-upsell-item]');
    var anyVisible = false;

    items.forEach(function (item) {
      var productId = parseInt(item.dataset.productId, 10);
      var hide = inCartIds.indexOf(productId) !== -1;
      item.classList.toggle('is-hidden', hide);
      if (!hide) anyVisible = true;
    });

    upsellsEl.classList.toggle('is-hidden', !anyVisible);
  }

  // Variant select change handler — updates price display and button state
  upsellsEl.addEventListener('change', function (evt) {
    var select = evt.target.closest('[data-upsell-variant-select]');
    if (!select) return;

    var item      = select.closest('[data-upsell-item]');
    var variantId = parseInt(select.value, 10);
    var variants  = JSON.parse(item.dataset.variants || '[]');
    var variant   = variants.find(function (v) { return v.id === variantId; });
    if (!variant) return;

    // Update ATC button
    var btn = item.querySelector('[data-upsell-atc]');
    if (btn) {
      btn.dataset.variantId = variantId;
      btn.disabled = !variant.available;
      btn.textContent = variant.available
        ? btn.dataset.defaultText
        : btn.dataset.soldOutText;
    }

    // Update price
    var priceAmount  = item.querySelector('[data-upsell-price-amount]');
    var compareEl    = item.querySelector('[data-upsell-compare-price]');
    var priceWrapper = item.querySelector('[data-upsell-price]');

    if (priceAmount) {
      priceAmount.textContent = theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat);
    }
    if (priceWrapper) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (!compareEl) {
          compareEl = document.createElement('s');
          compareEl.className = 'eg-cart-upsells__compare-price';
          compareEl.dataset.upsellComparePrice = '';
          priceWrapper.insertBefore(compareEl, priceWrapper.firstChild);
        }
        compareEl.textContent = theme.Currency.formatMoney(variant.compare_at_price, theme.settings.moneyFormat);
        compareEl.hidden = false;
      } else if (compareEl) {
        compareEl.hidden = true;
      }
    }
  });

  // Add to cart handler for each upsell button
  upsellsEl.addEventListener('click', function (evt) {
    var btn = evt.target.closest('[data-upsell-atc]');
    if (!btn || btn.disabled) return;

    // Prefer the selected variant from the select, fall back to data attribute
    var item   = btn.closest('[data-upsell-item]');
    var select = item && item.querySelector('[data-upsell-variant-select]');
    var variantId = select ? parseInt(select.value, 10) : parseInt(btn.dataset.variantId, 10);

    btn.disabled = true;
    btn.classList.add('btn--loading');

    fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    })
    .then(function (res) { return res.json(); })
    .then(function (data) {
      btn.classList.remove('btn--loading');
      if (data.status === 422) {
        btn.disabled = false;
        return;
      }
      // Re-enable as a fallback in case the item isn't hidden quickly
      setTimeout(function () {
        btn.disabled = false;
      }, 3000);
      // Trigger Motion's native cart rebuild + cart:updated flow
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: data } }));
    })
    .catch(function () {
      btn.disabled = false;
      btn.classList.remove('btn--loading');
    });
  });

  // Keep in sync whenever the cart changes
  document.addEventListener('cart:updated', function (evt) {
    var cart = evt.detail && evt.detail.cart;
    if (!cart) return;
    updateUpsells(cart);
  });

  // cart:updated is NOT fired after ajaxProduct:added, so fetch cart directly on add
  document.addEventListener('ajaxProduct:added', function () {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateUpsells(cart); });
  });
})();


/*
  Selling plan (Subscription) type selector
*/
document.querySelectorAll('.product-section').forEach((productSection) => {
  const typeInputs = productSection.querySelectorAll('[data-selling-plan-type-picker]');
  if (!typeInputs.length) return;

  const deliveryEveryWrapper = productSection.querySelector('.selling-plan-delivery-every-wrapper');
  const sellingPlanPickers = productSection.querySelectorAll('[data-selling-plan-picker]');
  const productForm = productSection.querySelector('form.product-single__form');

  function findQuantitySelector(optionsToShow) {
    const select = Array.from(productSection.querySelectorAll('.variant-wrapper select')).find((select) => {
      return Array.from(select.options).some((option) => optionsToShow.includes(option.value.trim()));
    });

    if (select) {
      return {
        type: 'select',
        element: select,
        options: Array.from(select.options)
      };
    }

    const fieldset = Array.from(productSection.querySelectorAll('.variant-input-wrap')).find((fieldset) => {
      return Array.from(fieldset.querySelectorAll('[data-variant-input]')).some((input) => {
        return optionsToShow.includes(input.value.trim());
      });
    });

    if (!fieldset) return null;

    return {
      type: 'radio',
      element: fieldset,
      options: Array.from(fieldset.querySelectorAll('[data-variant-input]'))
    };
  }

  function setSellingPlanPickersDisabled(disabled) {
    sellingPlanPickers.forEach((picker) => {
      // One-time purchases should not submit a selling_plan value.
      picker.disabled = disabled;
    });
  }

  function applySellingPlanType(selectedType, forcePreferredQuantity = true) {
    if (deliveryEveryWrapper) {
      deliveryEveryWrapper.hidden = selectedType !== 'subscription';
    }

    if (sellingPlanPickers.length) {
      setSellingPlanPickersDisabled(selectedType !== 'subscription');
    }

    // Hide certain variant options from the "Quantity" variant option
    const optionsToShow = ['30 Pack', '90 Pack'];
    const quantitySelector = findQuantitySelector(optionsToShow);

    if (!quantitySelector) return;

    quantitySelector.options.forEach((option) => {
      const value = option.value.trim();
      const shouldHide = selectedType === 'subscription' && !optionsToShow.includes(value);

      if (selectedType === 'subscription') {
        option.hidden = shouldHide;

        if (quantitySelector.type === 'radio') {
          const optionWrapper = option.closest('.variant-input');
          if (optionWrapper) optionWrapper.hidden = shouldHide;

          if (shouldHide && !option.disabled) {
            option.disabled = true;
            option.dataset.sellingPlanDisabled = 'true';
          }
        }
      } else {
        option.hidden = false;

        if (quantitySelector.type === 'radio') {
          const optionWrapper = option.closest('.variant-input');
          if (optionWrapper) optionWrapper.hidden = false;

          if (option.dataset.sellingPlanDisabled === 'true') {
            option.disabled = false;
            delete option.dataset.sellingPlanDisabled;
          }
        }
      }
    });

    const preferredValue = selectedType === 'subscription' ? '30 Pack' : '8 Pack';
    const hasPreferred = quantitySelector.options.some((option) => option.value === preferredValue && !option.hidden && !option.disabled);

    if (forcePreferredQuantity && hasPreferred) {
      if (quantitySelector.type === 'select') {
        quantitySelector.element.value = preferredValue;
        quantitySelector.element.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        const preferredOption = quantitySelector.options.find((option) => option.value === preferredValue);
        preferredOption.checked = true;
        preferredOption.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const firstAvailable = quantitySelector.options.find((option) => !option.hidden && !option.disabled);
    const selectedOption = quantitySelector.options.find((option) => {
      if (quantitySelector.type === 'select') {
        return option.selected;
      }

      return option.checked;
    });

    if (firstAvailable && (!selectedOption || selectedOption.hidden || selectedOption.disabled)) {
      if (quantitySelector.type === 'select') {
        quantitySelector.element.value = firstAvailable.value;
        quantitySelector.element.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        firstAvailable.checked = true;
        firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  typeInputs.forEach((input) => {
    input.addEventListener('change', () => {
      applySellingPlanType(input.value);
    });
  });

  if (productForm && sellingPlanPickers.length) {
    productForm.addEventListener('submit', () => {
      const checkedType = productSection.querySelector('[data-selling-plan-type-picker]:checked');
      const selectedType = checkedType ? checkedType.value : 'one-time';
      setSellingPlanPickersDisabled(selectedType !== 'subscription');
    });
  }

  const checkedType = productSection.querySelector('[data-selling-plan-type-picker]:checked');
  applySellingPlanType(checkedType ? checkedType.value : 'one-time', false);
});






/*
  registered and trademark symbol  resizing
*/

document.querySelectorAll('.product-single__title, .product__title, .grid-product__title, .h1, h1.product-single__title').forEach(el => {
  el.innerHTML = el.innerHTML.replace(/®/g, '<sup class="trademark-auto">®</sup>');
  el.innerHTML = el.innerHTML.replace(/™/g, '<sup class="trademark-auto">™</sup>');
});